import { useState, useEffect } from 'react'
import { servicesAPI, categoriesAPI, uploadFile } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, getImageUrl, confirmAction } from '../utils/helpers'
import CustomSelect from '../components/common/CustomSelect'

const Services = () => {
  const { showSuccess, showError } = useNotification()
  const [services, setServices] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [submittingService, setSubmittingService] = useState(false)
  const [submittingCategory, setSubmittingCategory] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [servicesRes, categoriesRes] = await Promise.all([
        servicesAPI.getAll(),
        categoriesAPI.getAll(),
      ])
      setServices(servicesRes.data.services || [])
      setCategories(categoriesRes.data.categories || [])
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleServiceSubmit = async (e, serviceId = null) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    setSubmittingService(true)
    
    try {
      const data = {
        title: formData.get('title'),
        description: formData.get('description'),
        price: formData.get('price'),
        icon: formData.get('icon') || 'fa-star',
        category_id: formData.get('category_id') ? Number(formData.get('category_id')) : null,
        sort_order: Number(formData.get('sort_order')) || 0,
        image: formData.get('image_path') || null,
      }

      const imageFile = formData.get('image')
      if (imageFile && imageFile.size > 0) {
        const uploadRes = await uploadFile(imageFile)
        const uploadedFilename = uploadRes.data.filename || uploadRes.data.image
        if (uploadedFilename) {
          data.image = uploadedFilename
        }
      }

      if (serviceId) {
        await servicesAPI.update(serviceId, data)
        showSuccess('Service updated successfully.')
      } else {
        await servicesAPI.create(data)
        showSuccess('Service added successfully.')
      }
      
      await fetchData()
      if (!serviceId) {
        e.target.reset()
      }
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setSubmittingService(false)
    }
  }

  const handleDeleteService = async (id) => {
    if (!confirmAction('Are you sure you want to delete this service?')) return
    
    try {
      await servicesAPI.delete(id)
      showSuccess('Service deleted.')
      await fetchData()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleCategorySubmit = async (e, categoryId = null) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    setSubmittingCategory(true)
    
    try {
      const data = {
        title: formData.get('cat_title'),
        description: formData.get('cat_description') || '',
        icon: formData.get('cat_icon') || 'fa-tag',
        sort_order: Number(formData.get('cat_sort_order')) || 0,
      }

      if (categoryId) {
        await categoriesAPI.update(categoryId, data)
        showSuccess('Category updated.')
      } else {
        await categoriesAPI.create(data)
        showSuccess('Category added.')
      }
      
      await fetchData()
      if (!categoryId) {
        e.target.reset()
      }
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setSubmittingCategory(false)
    }
  }

  const handleDeleteCategory = async (id) => {
    if (!confirmAction('Delete this category? Its services will become uncategorised.')) return
    
    try {
      await categoriesAPI.delete(id)
      showSuccess('Category deleted. Its services are now uncategorised.')
      await fetchData()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  return (
    <div className="admin-page">
      <h2 className="admin-title">Services &amp; Categories</h2>
      <p className="admin-sub">
        Manage your salon services and categories. Everything is saved to MySQL and immediately updates the website.
      </p>

      <div className="row">
        {/* Services column */}
        <div className="col-lg-8">
          <div className="panel">
            <div className="panel-head">
              <i className="fa fa-scissors" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Existing Services ({services.length})
            </div>
            {services.length > 0 ? (
              services.map((service) => (
                <div key={service.id} className="item-row" style={{alignItems: 'center', gap: '10px', padding: '12px 0', borderBottom: '1px solid var(--line)'}}>
                  {/* Service Thumbnail */}
                  <div style={{width: '54px', height: '54px', minWidth: '54px', borderRadius: '6px', overflow: 'hidden', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--line)'}}>
                    {service.image ? (
                      <img src={getImageUrl(service.image)} alt={service.title} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    ) : (
                      <i className={`fa ${service.icon || 'fa-scissors'}`} style={{color: 'var(--gold-3)', fontSize: '20px'}}></i>
                    )}
                  </div>

                  {/* Inline edit form */}
                  <form onSubmit={(e) => handleServiceSubmit(e, service.id)} className="item-edit" style={{flex: 1, display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center'}}>
                    <input
                      type="number"
                      name="sort_order"
                      defaultValue={service.sort_order}
                      className="form-control form-control-sm"
                      style={{width: '50px'}}
                      title="Sort Order"
                    />
                    <input
                      type="text"
                      name="title"
                      defaultValue={service.title}
                      className="form-control form-control-sm"
                      style={{flex: '1 1 140px'}}
                      required
                      placeholder="Title"
                      title="Title"
                    />
                    <input
                      type="text"
                      name="price"
                      defaultValue={service.price || ''}
                      className="form-control form-control-sm"
                      style={{width: '90px'}}
                      placeholder="Price (e.g. $45)"
                      title="Price"
                    />
                    <div style={{width: '130px'}}>
                      <CustomSelect
                        name="category_id"
                        defaultValue={String(service.category_id || '')}
                        placeholder="No category"
                        small
                        options={[
                          { value: '', label: 'No category' },
                          ...categories.map(cat => ({ value: String(cat.id), label: cat.title }))
                        ]}
                      />
                    </div>
                    <input
                      type="text"
                      name="icon"
                      defaultValue={service.icon || 'fa-scissors'}
                      className="form-control form-control-sm"
                      style={{width: '90px'}}
                      placeholder="Icon"
                      title="FontAwesome icon class"
                    />
                    <input
                      type="text"
                      name="description"
                      defaultValue={service.description || ''}
                      className="form-control form-control-sm"
                      style={{flex: '2 1 100%'}}
                      placeholder="Short description"
                      title="Description"
                    />
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px', width: '100%', marginTop: '4px'}}>
                      <label style={{fontSize: '11px', color: 'var(--muted)', margin: 0}}>New Image:</label>
                      <input type="file" name="image" className="form-control form-control-sm" accept="image/*" style={{flex: 1}} />
                      <input type="hidden" name="image_path" value={service.image || ''} />
                      <button type="submit" className="btn btn-sm btn-gold" title="Save changes">
                        <i className="fa fa-check"></i> Save
                      </button>
                    </div>
                  </form>

                  <button
                    onClick={() => handleDeleteService(service.id)}
                    className="btn btn-sm btn-danger"
                    title="Delete service"
                    style={{alignSelf: 'center'}}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              ))
            ) : (
              <p className="muted" style={{padding: '16px 0'}}>No services yet. Add your first service below.</p>
            )}
          </div>

          {/* Add Service Panel */}
          <div className="panel mt-3">
            <div className="panel-head">
              <i className="fa fa-plus" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Add New Service
            </div>
            <form onSubmit={handleServiceSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Title *</label>
                    <input type="text" name="title" className="form-control" required placeholder="e.g. Haircut & Styling" />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Price</label>
                    <input type="text" name="price" className="form-control" placeholder="e.g. from $45" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea name="description" className="form-control" rows="2" placeholder="Describe this service..."></textarea>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Category</label>
                    <CustomSelect
                      name="category_id"
                      placeholder="Select Category"
                      options={[
                        { value: '', label: 'No category' },
                        ...categories.map(cat => ({ value: String(cat.id), label: cat.title }))
                      ]}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Icon class</label>
                    <input type="text" name="icon" className="form-control" defaultValue="fa-scissors" placeholder="fa-scissors" />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Display order</label>
                    <input type="number" name="sort_order" className="form-control" defaultValue="1" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Service Image</label>
                <input type="file" name="image" className="form-control-file" accept="image/*" />
              </div>

              <button type="submit" className="btn btn-gold btn-block" disabled={submittingService}>
                <i className="fa fa-plus-circle"></i> {submittingService ? 'Adding...' : 'Add Service'}
              </button>
            </form>
          </div>
        </div>

        {/* Categories column */}
        <div className="col-lg-4">
          <div className="panel">
            <div className="panel-head">
              <i className="fa fa-tags" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Service Categories ({categories.length})
            </div>
            {categories.length > 0 ? (
              categories.map((category) => (
                <div key={category.id} className="item-row" style={{padding: '8px 0', borderBottom: '1px solid var(--line)'}}>
                  <form onSubmit={(e) => handleCategorySubmit(e, category.id)} className="item-edit" style={{flex: 1, display: 'flex', gap: '4px', alignItems: 'center'}}>
                    <input type="number" name="cat_sort_order" defaultValue={category.sort_order} className="form-control form-control-sm" style={{width: '46px'}} title="Order" />
                    <input type="text" name="cat_title" defaultValue={category.title} className="form-control form-control-sm" required style={{flex: 1}} />
                    <input type="text" name="cat_icon" defaultValue={category.icon || 'fa-tag'} className="form-control form-control-sm" style={{width: '76px'}} placeholder="fa-tag" />
                    <button type="submit" className="btn btn-sm btn-gold" title="Save category"><i className="fa fa-check"></i></button>
                  </form>
                  <button onClick={() => handleDeleteCategory(category.id)} className="btn btn-sm btn-danger" title="Delete category">
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              ))
            ) : (
              <p className="muted">No categories yet.</p>
            )}
          </div>

          <div className="panel mt-3">
            <div className="panel-head">
              <i className="fa fa-plus" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Add Category
            </div>
            <form onSubmit={handleCategorySubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input type="text" name="cat_title" className="form-control" required placeholder="e.g. Hair Care" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input type="text" name="cat_description" className="form-control" placeholder="Short description" />
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="form-group">
                    <label>Icon class</label>
                    <input type="text" name="cat_icon" className="form-control" defaultValue="fa-tag" placeholder="fa-tag" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Display order</label>
                    <input type="number" name="cat_sort_order" className="form-control" defaultValue="1" />
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-gold btn-block" disabled={submittingCategory}>
                <i className="fa fa-plus-circle"></i> {submittingCategory ? 'Adding...' : 'Add Category'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Services
